/* eslint-disable react/no-unused-prop-types */
import { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { arrayOf, object, shape, string } from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';

import Filter from '../../page-framework/filter/FilterDecorator';
import { filterPropTypes, filterPropDefaults } from '../../page-framework/filter/FilterPropTypes';
import { useEntityService } from '../../../services/EntityService';
import { LegacyIcon } from '../../icon/Legacy';
import TagFilterStateServiceFactory, { AND, OR, TAG_OP_SUMMARIES, title as filterTitle } from './TagFilterStateService';

export const filterId = 'Tags';

const useStyles = makeStyles((theme) => ({
    root: {
        '& span.label': {
            fontSize: 16,
            fontWeight: 800,
        },
    },

    radioGroupLabel: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,

        '& span.label': {
            marginRight: theme.spacing(2),
        },
    },

    radioGroup: {
        ...theme.mixins.flex.displayInlineFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentStart,
    },

    tagField: {
        marginBottom: theme.spacing(2),
    },

    tagScrollContainer: {
        marginTop: theme.spacing(1),
    },

    tagContainer: {
        width: 500,
        maxHeight: 300,

        '& .MuiChip-root': {
            marginTop: theme.spacing(1),
            marginRight: theme.spacing(1),
            paddingLeft: theme.spacing(1),

            '& .MuiIcon-root': {
                fontSize: '1.2em',
                marginRight: 0,
                color: theme.palette.grey[theme.palette.type === 'light' ? 700 : 100],
            },

            '& .MuiChip-label': {
                paddingLeft: theme.spacing(1),
            },
        },
    },
}));

function updateAvailableTags(allTags, selectedTags) {
    return allTags.filter((tag) => !_.includes(selectedTags, tag.tag));
}

function TagFilter({ state, filterActions, defaultTags }) {
    const classes = useStyles();
    const [tags, setTags] = useState(state?.tags || []);
    const [tagOp, setTagOp] = useState(state?.tagOp || AND);
    const [allTags, setAllTags] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const entityService = useEntityService();

    const getAllTags = useCallback(() => {
        if (defaultTags) return Promise.resolve(defaultTags);
        return entityService.getEntities('tags', { rowLimit: 1000 }).then((response) => response.data);
    }, [entityService, defaultTags]);

    useEffect(() => {
        getAllTags().then((loadedTags) => {
            const sortedTags = _.sortBy(loadedTags, (it) => it?.tag?.toLowerCase());
            setAllTags(sortedTags);
            setTagOptions(_.map(sortedTags, 'tag'));
        });
    }, [getAllTags]);

    useEffect(() => {
        setAvailableTags(updateAvailableTags(allTags, tags));
    }, [allTags, tags]);

    useEffect(() => {
        filterActions.setValid(tags.length > 0);
    }, [tags, filterActions]);

    function handleTagOpChange(event) {
        const newTagOp = event.target.value;
        setTagOp(newTagOp);
        filterActions.setState({
            tags,
            tagOp: newTagOp,
        });
    }

    function handleTagsChanged(event, value) {
        setTags(value);
        setAvailableTags(updateAvailableTags(allTags, value));
        filterActions.setState({
            tags: value,
            tagOp,
        });
    }

    function handleTagClick(tag) {
        const selectedTags = [...tags, tag.tag];
        setTags(selectedTags);
        setAvailableTags(updateAvailableTags(allTags, selectedTags));
        filterActions.setState({
            tags: selectedTags,
            tagOp,
        });
    }

    function getIcon(tag) {
        const ICON = {
            site: 'room',
            businessservice: 'cloud',
        };

        return (
            <>
                {tag?.types?.map((type) =>
                    ICON[type] ? <LegacyIcon key={type} icon={ICON[type]} fontSize="small" /> : null
                )}
            </>
        );
    }

    return (
        <div className={clsx('flex-container', classes.root)}>
            <div className={classes.radioGroupLabel}>
                <span className="label">
                    <Trans>Match Selected Tags</Trans>:
                </span>
                <RadioGroup className={classes.radioGroup} name="tagOp" value={tagOp} onChange={handleTagOpChange}>
                    <FormControlLabel value={OR} control={<Radio />} label={i18n._(TAG_OP_SUMMARIES[OR])} />
                    <FormControlLabel value={AND} control={<Radio />} label={i18n._(TAG_OP_SUMMARIES[AND])} />
                </RadioGroup>
            </div>
            <Autocomplete
                className={classes.tagField}
                multiple
                autoHighlight
                autoSelect
                autoFocus
                filterSelectedOptions
                options={tagOptions}
                value={tags}
                onChange={handleTagsChanged}
                renderInput={(params) => <TextField {...params} placeholder={i18n._(t`Add Tag`)} />}
            />
            <span className="label">
                <Trans>Available Tags</Trans>
            </span>
            <div className={clsx('scroll-container', classes.tagScrollContainer)}>
                <div className={classes.tagContainer}>
                    {availableTags?.map((tag) => (
                        <Chip key={tag.tag} icon={getIcon(tag)} label={tag.tag} onClick={() => handleTagClick(tag)} />
                    ))}
                </div>
            </div>
        </div>
    );
}

TagFilter.propTypes = {
    id: filterPropTypes.id,
    title: filterPropTypes.title,
    filterActions: filterPropTypes.filterActions,
    state: shape({
        tags: arrayOf(string).isRequired,
        tagOp: string.isRequired,
    }),
    // Only used for testing.
    defaultTags: arrayOf(object),
};

TagFilter.defaultProps = {
    ...filterPropDefaults,
    id: filterId,
    title: filterTitle,
    state: undefined,
};

export default Filter({
    filterId,
    filterStateServiceFactory: TagFilterStateServiceFactory,
})(TagFilter);
